<template>
  <div>
    <div
      v-if="isProgramMastered && !tiny"
      class="col-12"
    >
      <banner
        type="success"
        show-icon
        :message="$t('run.collectData.setProgramMeetingCriteria')"
        class="q-mb-md"
      />
    </div>
    <div
      :class="{
        'no-wrap': dense && tiny,
        'q-col-gutter-sm': dense && tiny,
        'q-col-gutter-md': !dense || !tiny
      }"
      class="row"
    >
      <span
        class="items-end row"
      >
        <q-card
          class="col-auto column full-height noselect"
          :class="{'bg-green-2': currentMode === 'mastered',
                   'bg-yellow-2': currentMode === 'probe',
                   'bg-red-2': currentMode === 'acquisition',
          }"
        >
          <q-card-section
            v-if="isAnalyzingData"
            class="col row items-center text-center"
          >
            <q-spinner
              size="xs"
              class="q-mr-md"
            /> {{ $t('run.collectData.textAnalyzing') }}
          </q-card-section>
          <template v-else>
            <q-card-section
              v-if="!tiny"
              class="col row items-center text-center q-pb-none"
            >
              <q-badge
                floating
              >
                {{ $t('run.collectData.textCurrentStreak', {currentStreak}) }}
              </q-badge>
              <div
                class="text-dark-variant text-overline text-uppercase"
              >
                {{ $t('run.collectData.textCurrentMode', {currentMode}) }}
              </div>
            </q-card-section>
            <q-card-section
              class="col row items-center justify-center"
              :class="{
                'q-pt-xs': !tiny,
              }"
            >
              <q-badge
                v-if="tiny"
                floating
              >
                {{ $t('run.collectData.textCurrentStreak', {currentStreak}) }}
              </q-badge>
              {{ currentModeLabel }}
            </q-card-section>
          </template>
        </q-card>
      </span>
      <span
        v-for="target in currentTargets"
        :key="target.id"
      >
        <target-data
          ref="targets"
          :session-program="sessionProgram"
          :target="target"
          :data="data"
          :active-prompt-levels="activePromptLevels"
          :dense="dense"
          :tiny="tiny"
          :disable="disable"
          :dimmed="isAnalyzingData"
          :workflow-statuses="workflowStatuses"
          :session-paused="sessionPaused"
          :session-duration-offset="sessionDurationOffset"
          @record="onRecordData"
          @record-program-data="onRecordProgramData"
        />
      </span>
    </div>
  </div>
</template>
<script>
import TargetData from 'components/Programs/DataCollection/TargetData';
import Banner from 'components/UI/Banner';
import {
  chain, every, filter, find, get, head, includes, isEmpty, size,
} from 'lodash';
import DataCollectionProgram from 'src/mixins/DataCollectionProgram';
import Interactions from 'src/mixins/Interactions';
import Targets from 'src/mixins/Targets';

export default {

  name: 'SetProgram',

  components: { TargetData, Banner },

  mixins: [DataCollectionProgram, Interactions, Targets],

  emits: ['record-program-data', 'record', 'change-target-status'],

  data() {
    return {
      masteringCriteria: {
        probe: 5,
        acquisition: 3,
        mastered: 5,
      },

      isAnalyzingData: false,
    };
  },

  computed: {

    currentStreak() {
      return `${chain(this.data)
        .get('data')
        .cloneDeep()
        .reverse()
        .transform((result, dataItem) => {
          if ((dataItem.mode === this.currentMode)
            && (dataItem.scoreValue === 100)) {
            result.push(1);
          } else {
            return false;
          }
        })
        .sum()
        .value()} / ${get(this.masteringCriteria, this.currentMode, '?')}`;
    }, // currentStreak()

    currentTarget() {
      return get(this.data, 'currentTarget', null);
    }, // currentTarget()

    currentTargets() {
      return filter(this.sortedTargets, { id: this.currentTarget });
    }, // currentTargets()

    currentMode() {
      return get(this.data, 'currentMode', null);
    },

    currentModeLabel() {
      if (this.currentMode === 'null') {
        return '';
      }
      return get(this.$tm('run.collectData.setModes'), this.currentMode, '');
    },

    isProgramMastered() {
      // Program is mastered when the learner were immediately mastered 5 unknown targets in a row
      const lastTrials = chain(this.data)
        .get('data')
        .takeRight(this.masteringCriteria.mastered)
        .filter({ mode: 'probe' })
        .value();

      return ((size(lastTrials) === this.masteringCriteria.mastered)
              && (every(lastTrials, { scoreValue: 100 })));
    },

  },

  created() {
    if (!includes(['probe', 'acquisition', 'mastered'], this.currentMode)) {
      if (!chain(this.sortedTargets)
        .filter({ status: 'acquisition' })
        .isEmpty()
        .value()) {
        this.switchMode('acquisition', true);
      } else {
        this.switchMode('probe', true);
      }
      return;
    }

    if (isEmpty(this.currentTargets)) {
      this.switchMode(this.currentMode, true);
    }
  },

  methods: {

    finishSession() {
      const finalData = chain(this.$refs)
        .get('targets')
        .map((targetRef) => {
          if (chain(targetRef).get('finishSession').isFunction().value()) {
            return targetRef.finishSession();
          }
          return null;
        })
        .flatten()
        .compact()
        .map((trialData) => ({
          ...trialData,
        }))
        .value();

      return {
        finalData,
      };
    }, // finishSession()

    undoData() {
      const lastData = chain(this.data).get('data').last().value();
      if (lastData) {
        const prevMode = lastData.mode || null;
        const prevTargetId = lastData.target || null;

        const target = find(this.sortedTargets, { id: prevTargetId });
        if (get(target, 'status') !== prevMode) {
          this.changeTargetStatus(target, prevMode);
        }

        const programData = {
          currentTarget: prevTargetId,
          currentMode: prevMode,
        };
        this.$emit('record-program-data', this.sessionProgram, programData);
      }

      return true;
    },

    onRecordData(sessionProgram, target, data) {
      const saveData = { ...data, mode: this.currentMode };
      this.analyzeData(chain(this.data).get('data').concat({ ...saveData, target: target.id }).value());

      this.$emit('record', sessionProgram, target, saveData);
    }, // onRecordData()

    onRecordProgramData(sessionProgram, dataObj) {
      this.$emit('record-program-data', sessionProgram, dataObj);
    },

    async analyzeData(data) {
      this.isAnalyzingData = true;

      const PROBE_MASTERING_TRIALS = 1;
      try {
        switch (this.currentMode) {
          case 'probe': {
            const lastTrials = chain(data)
              .takeRight(PROBE_MASTERING_TRIALS)
              .filter({ target: this.currentTarget })
              .value();

            const currentTarget = head(this.currentTargets);
            if ((size(lastTrials) === PROBE_MASTERING_TRIALS)
              && (every(lastTrials, { scoreValue: 100 }))) {
              await this.masterTarget(currentTarget);
              this.switchMode('probe', true);
              return;
            }
            // Switch to acquisition mode but on the same target
            await this.teachTarget(currentTarget);
            this.switchMode('acquisition', false);
            break;
          }
          case 'acquisition': {
            const lastTrialsScore = chain(data)
              .takeRight(this.masteringCriteria.acquisition)
              .filter({ target: this.currentTarget })
              .filter({ mode: 'acquisition' })
              .map('scoreValue')
              .sum()
              .value();
            if (lastTrialsScore < this.masteringCriteria.acquisition * 100) {
              return;
            }

            const currentTarget = head(this.currentTargets);
            await this.masterTarget(currentTarget);
            this.switchMode('mastered', true);
            break;
          }
          case 'mastered': {
            const lastTrials = chain(data)
              .takeRight(this.masteringCriteria.mastered)
              .filter({ mode: 'mastered' })
              .value();

            if ((size(lastTrials) === this.masteringCriteria.mastered)
              && (every(lastTrials, { scoreValue: 100 }))) {
              this.switchMode('probe', true);
              return;
            }

            if (chain(data).last().get('scoreValue').value() === 100) {
              const programData = {
                currentTarget: this.getNewTarget('mastered'),
              };
              this.$emit('record-program-data', this.sessionProgram, programData);
              return;
            }
            // Switch to acquisition mode but on the same target
            const currentTarget = head(this.currentTargets);
            await this.teachTarget(currentTarget);
            this.switchMode('acquisition', false);

            break;
          }
          default: {
            break;
          }
        }
      } finally {
        this.isAnalyzingData = false;
      }
    },

    switchMode(newMode, refreshTarget = false) {
      let currentMode = newMode;
      if ((currentMode === 'mastered') && (chain(this.sortedTargets)
        .filter({ status: 'mastered' })
        .size()
        .value() < 2)) {
        // If we don't yet have two mastered targets then go to probe-mode
        currentMode = 'probe';
      }

      const programData = {
        currentMode,
        ...(refreshTarget ? { currentTarget: this.getNewTarget(currentMode) } : {}),
      };
      this.$emit('record-program-data', this.sessionProgram, programData);
    }, // switchMode()

    teachTarget(target) {
      return this.changeTargetStatus(target, 'acquisition');
    },

    async masterTarget(target) {
      await this.changeTargetStatus(target, 'mastered');

      // Only probe new targets if we have no targets in probe mode already
      if (chain(this.sortedTargets)
        .filter({ status: 'probe' })
        .isEmpty()
        .value()) {
        const targetToOpen = chain(this.sessionProgram)
          .get(['program', 'targets'])
          .filter({ parentTarget: get(target, 'parentTarget', null) })
          .filter({ status: 'waiting' })
          .sortBy('order')
          .head()
          .value();

        if (targetToOpen) {
          await this.changeTargetStatus(targetToOpen, 'probe');
          this.notifySuccess(this.$tc('run.collectData.proceedSuccess', 1, {
            targetLabels: targetToOpen.label,
          }));
        }
      }
    }, // masterTarget()

    changeTargetStatus(target, toStatus) {
      return new Promise((resolve) => {
        this.$emit('change-target-status', target, toStatus, () => {
          resolve();
        });
      });
    },

    getNewTarget(status) {
      const targetsInStatus = filter(this.sortedTargets, { status });
      return get(targetsInStatus, [Math.floor(Math.random() * size(targetsInStatus)), 'id'], null);
    }, // getNewTarget()

  },
};
</script>
